import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlaylistManagementActions = ({ syncResult, onAction }) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');

  const handleScheduleSync = () => {
    onAction('schedule', { frequency: scheduleFrequency });
    setShowScheduleModal(false);
  };

  const handleShare = () => {
    const shareText = `Just synced ${syncResult.successCount} out of ${syncResult.totalTracks} songs from ${syncResult.playlistName} to ${syncResult.destinationPlatform}! 🎵`;
    
    if (navigator.share) {
      navigator.share({
        title: 'PlaylistSync Results',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      // Show toast notification
    }
  };

  return (
    <div className="space-y-4">
      {/* Primary Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="primary"
          iconName="ExternalLink"
          iconSize={16}
          onClick={() => onAction('viewPlaylist')}
          className="flex-1"
        >
          View Synced Playlist
        </Button>
        <Button
          variant="outline"
          iconName="RotateCcw"
          iconSize={16}
          onClick={() => onAction('syncAgain')}
          className="flex-1"
        >
          Sync Again
        </Button>
      </div>

      {/* Secondary Actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Button
          variant="ghost"
          iconName="ArrowLeftRight"
          iconSize={16}
          onClick={() => onAction('reverseSync')}
          className="text-sm"
        >
          Reverse Sync
        </Button>
        <Button
          variant="ghost"
          iconName="Calendar"
          iconSize={16}
          onClick={() => setShowScheduleModal(true)}
          className="text-sm"
        >
          Schedule Sync
        </Button>
        <Button
          variant="ghost"
          iconName="Download"
          iconSize={16}
          onClick={() => onAction('exportLogs')}
          className="text-sm"
        >
          Export Logs
        </Button>
        <Button
          variant="ghost"
          iconName="Share"
          iconSize={16}
          onClick={handleShare}
          className="text-sm"
        >
          Share Results
        </Button>
      </div>

      {/* Rollback Section */}
      {syncResult.canRollback && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-text-primary mb-1">
                Rollback Available
              </h4>
              <p className="text-sm text-text-secondary mb-3">
                You can undo this sync and restore the playlist to its previous state.
              </p>
              <Button
                variant="warning"
                size="sm"
                iconName="Undo"
                iconSize={14}
                onClick={() => onAction('rollback')}
              >
                Rollback Sync
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1020 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-elevation-3 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">Schedule Regular Sync</h3>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="p-1 rounded-md hover:bg-surface-secondary transition-colors duration-200"
                >
                  <Icon name="X" size={20} className="text-text-muted" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Sync Frequency
                  </label>
                  <select
                    value={scheduleFrequency}
                    onChange={(e) => setScheduleFrequency(e.target.value)}
                    className="w-full p-3 bg-background border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-text-secondary">
                      Scheduled syncs will automatically transfer new songs added to your source playlist.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    variant="ghost"
                    onClick={() => setShowScheduleModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleScheduleSync}
                    className="flex-1"
                  >
                    Schedule
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistManagementActions;